const STORAGE_KEY = 'xkey-in-app-history';
const MAX_ENTRIES = 40;

const listeners = new Set<() => void>();

export function subscribeInAppBack(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyInAppBack() {
  listeners.forEach((listener) => listener());
}

type StackEntry = { key: string; path: string };
type NavType = 'POP' | 'PUSH' | 'REPLACE';

function readStack(): StackEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is StackEntry =>
        !!entry &&
        typeof entry === 'object' &&
        typeof (entry as StackEntry).key === 'string' &&
        typeof (entry as StackEntry).path === 'string',
    );
  } catch {
    return [];
  }
}

function writeStack(stack: StackEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack.slice(-MAX_ENTRIES)));
  } catch {
    // Private mode can reject storage. The button stays hidden for this visit.
  }
}

/** Same-origin referrer that is a different page. A reload's referrer is the current URL. */
export function inAppReferrerPath(referrer: string, currentOrigin: string, currentPath: string): string | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (url.origin !== currentOrigin) return null;
    const path = `${url.pathname}${url.search}`;
    if (path === currentPath) return null;
    return path;
  } catch {
    return null;
  }
}

/**
 * Remember in-app pages so the breadcrumb back control can return to the previous one.
 * The stack survives a refresh in the same tab.
 */
export function recordInAppNavigation(args: {
  key: string;
  path: string;
  navType: NavType;
  referrerPath?: string | null;
}) {
  let stack = readStack();

  if (stack.length === 0) {
    if (args.referrerPath) stack = [{ key: 'referrer', path: args.referrerPath }];
    stack = [...stack, { key: args.key, path: args.path }];
    writeStack(stack);
    notifyInAppBack();
    return;
  }

  if (args.navType === 'REPLACE') {
    stack[stack.length - 1] = { key: args.key, path: args.path };
    writeStack(stack);
    notifyInAppBack();
    return;
  }

  if (args.navType === 'POP') {
    const byKey = stack.findIndex((entry) => entry.key === args.key && entry.path === args.path);
    if (byKey >= 0) {
      stack = stack.slice(0, byKey + 1);
    } else {
      const byPath = stack.findLastIndex((entry) => entry.path === args.path);
      if (byPath >= 0) stack = stack.slice(0, byPath + 1);
      else stack = [...stack, { key: args.key, path: args.path }];
    }
    writeStack(stack);
    notifyInAppBack();
    return;
  }

  const previous = stack.length >= 2 ? stack[stack.length - 2] : undefined;
  if (previous?.path === args.path && stack[stack.length - 1]?.path !== args.path) {
    stack = stack.slice(0, -1);
    stack[stack.length - 1] = { key: args.key, path: args.path };
  } else if (stack[stack.length - 1]?.path === args.path) {
    stack[stack.length - 1] = { key: args.key, path: args.path };
  } else {
    stack = [...stack, { key: args.key, path: args.path }];
  }
  writeStack(stack);
  notifyInAppBack();
}

/** Browser history index from React Router. The first page in this tab is 0. */
export function canStepBackInHistory(historyIndex: number | null | undefined): boolean {
  return typeof historyIndex === 'number' && historyIndex > 0;
}

/** Path to show behind the current page, when that page was opened inside the app. */
export function previousInAppPath(currentPath: string): string | null {
  const stack = readStack();
  const index = stack.findLastIndex((entry) => entry.path === currentPath);
  if (index > 0) return differentPageBefore(stack, index);
  if (index === 0) return null;

  const currentPathname = pathnameOf(currentPath);
  for (let i = stack.length - 1; i >= 0; i--) {
    if (pathnameOf(stack[i].path) !== currentPathname) return stack[i].path;
  }
  return null;
}

function pathnameOf(path: string): string {
  return path.split('?')[0] ?? path;
}

/** Skip tab-only query changes so back leaves the page instead of the previous tab. */
function differentPageBefore(stack: StackEntry[], index: number): string | null {
  const currentPathname = pathnameOf(stack[index]?.path ?? '');
  for (let i = index - 1; i >= 0; i--) {
    if (pathnameOf(stack[i].path) !== currentPathname) return stack[i].path;
  }
  return null;
}

export function resetInAppBackForTests() {
  sessionStorage.removeItem(STORAGE_KEY);
}
